/**
 * Getting into a meeting as a screen: what the share-mode join answered, and
 * what to say when it refused.
 *
 * Pure, so the rules can be tested without a page: the dashboard's Screen Share
 * dialog asks with `check: true`, the share page joins for real, and both read
 * the server's answer through this. Contract §6 in
 * docs/screen-share-only-join-checkpoint.md.
 */
import {
  ApiError,
  type LobbyWait,
  type ShareJoinMeeting,
  type ShareJoinResult,
  type WaitingFor,
} from '@/features/meetings/services/meetings.api'
import { MEETING_ERROR_CODES } from '@/features/meetings/constants/meetings.constants'
import { describeSeriesRefusal } from '@/features/meetings/services/join-failure'
import type { Translate } from '@/features/meetings/utils/recurrence'
import type { RoomCredentials } from '@/features/meeting/store/meeting-session.store'
import { isShareOnlyPeer } from '@/features/meeting/services/share-only'

/** Why a share join did not go ahead. */
export type ShareJoinRefusal =
  | { kind: 'notFound' }
  | { kind: 'ended' }
  | { kind: 'cancelled' }
  /** A recurring series' link on a day it cannot open. */
  | { kind: 'series'; messageKey: string; nextStartTimeMs?: number }
  | { kind: 'full' }
  | { kind: 'rateLimited' }
  | { kind: 'invalidName' }
  /** A host removed this account's share page from the meeting (`403 SHARE_REMOVED`). */
  | { kind: 'removed' }
  /** Share mode cannot be used right now (or this server has none). */
  | { kind: 'unavailable' }
  /** Network, the server's own error, or anything we cannot name. */
  | { kind: 'failed' }

const SERIES = new Set<string>([
  MEETING_ERROR_CODES.OCCURRENCE_OVER,
  MEETING_ERROR_CODES.OCCURRENCE_CANCELLED,
  MEETING_ERROR_CODES.OCCURRENCE_NOT_TODAY,
  MEETING_ERROR_CODES.SERIES_ENDED,
])

export function classifyShareJoinError(err: unknown): ShareJoinRefusal {
  if (!(err instanceof ApiError)) return { kind: 'failed' }
  const { code, status } = err
  if (code === 'SHARE_MODE_UNAVAILABLE' || code === 'SIGNALING_UNAVAILABLE') return { kind: 'unavailable' }
  if (status >= 500) return { kind: 'failed' }
  // The waiting-room knock of a page a host removed: the same answer the room
  // channel gives (`share_removed`), said at the door.
  if (code === 'SHARE_REMOVED') return { kind: 'removed' }
  // Share mode never asks for a password. A server that does is one without
  // share mode, treating this as an ordinary join.
  if (code === MEETING_ERROR_CODES.INVALID_PASSWORD) return { kind: 'unavailable' }
  if (code === 'INVALID_NAME') return { kind: 'invalidName' }
  if (code === 'RATE_LIMITED' || status === 429) return { kind: 'rateLimited' }
  if (code === MEETING_ERROR_CODES.MEETING_FULL) return { kind: 'full' }
  if (SERIES.has(code)) {
    const next = err.details?.nextStartTimeMs
    return {
      kind: 'series',
      messageKey: code,
      ...(typeof next === 'number' && Number.isFinite(next) ? { nextStartTimeMs: next } : {}),
    }
  }
  if (code === MEETING_ERROR_CODES.MEETING_CANCELLED) return { kind: 'cancelled' }
  if (code === MEETING_ERROR_CODES.MEETING_ENDED) return { kind: 'ended' }
  if (code === 'NOT_FOUND' || code === MEETING_ERROR_CODES.MEETING_NOT_FOUND || status === 404) {
    return { kind: 'notFound' }
  }
  // Unlike the ordinary join, an id share mode does not know is answered 404,
  // never with a room. Anything else is not "not found".
  return { kind: 'failed' }
}

export function describeShareJoinRefusal(refusal: ShareJoinRefusal, t: Translate, language: string): string {
  switch (refusal.kind) {
    case 'notFound': return t('meetings.errors.MEETING_NOT_FOUND')
    case 'ended': return t('meetings.errors.MEETING_ENDED')
    case 'cancelled': return t('meetings.errors.MEETING_CANCELLED')
    case 'series': return describeSeriesRefusal(refusal, t, language)
    case 'full': return t('meetings.errors.MEETING_FULL')
    case 'rateLimited': return t('shareOnly.rateLimited')
    case 'invalidName': return t('shareOnly.nameInvalidChars')
    case 'removed': return t('shareOnly.removedShare')
    case 'unavailable': return t('shareOnly.unavailable')
    case 'failed': return t('meetings.couldNotJoinMeeting')
  }
}

/**
 * What a failed waiting-room poll means: `expired` when the request is gone, a
 * refusal when the server let it in but cannot make it a share session, or
 * null for a blip worth polling through.
 *
 * Only an answer that names itself settles anything. A 404 is a request that
 * is gone — swept after going unpolled (a hidden tab's throttled timers do
 * that), or whose room is gone — never a host's refusal: a refusal is a 200
 * `{status: 'denied'}`, and decided requests are not swept. So it is said as
 * expired, with a Try again, as on mobile. An admission signaling would not
 * sign as share-only is `SHARE_MODE_UNAVAILABLE` — and it answers that on
 * every poll, so treating it as a blip waited out the ten minutes and then
 * said nobody had let the page in, when the host had. Network failures,
 * `SIGNALING_UNAVAILABLE` and the server's own errors are worth another poll.
 */
export function shareLobbyPollFailure(err: unknown): 'expired' | ShareJoinRefusal | null {
  if (!(err instanceof ApiError)) return null
  if (err.status === 404) return 'expired'
  if (err.code === 'SHARE_MODE_UNAVAILABLE') return { kind: 'unavailable' }
  return null
}

/**
 * Worth offering "Try again" for: the same request can go through later.
 * `removed` too — a host's Allow back lifts it, as the room's own removed
 * screen offers Rejoin for.
 */
export function shareJoinRetryable(refusal: ShareJoinRefusal): boolean {
  return refusal.kind === 'full' || refusal.kind === 'rateLimited'
    || refusal.kind === 'unavailable' || refusal.kind === 'failed'
    || refusal.kind === 'removed'
}

/** Where the dashboard dialog shows a refusal: on the field it is about, or under the form. */
export function shareRefusalField(refusal: ShareJoinRefusal): 'id' | 'name' | 'form' {
  if (refusal.kind === 'notFound') return 'id'
  if (refusal.kind === 'invalidName') return 'name'
  return 'form'
}

/**
 * What a `check: true` answer means for the dialog: null to go ahead, or why not.
 *
 * Only an answer that says `mode: 'share'` counts. A server without share mode
 * answers an ordinary check, and the page would then be joined as the account
 * itself — so it is refused here, before anyone leaves the dialog.
 */
export function shareCheckRefusal(res: ShareJoinResult): ShareJoinRefusal | null {
  if (res.mode !== 'share') return { kind: 'unavailable' }
  if (res.status === 'ok' && !res.meeting) return { kind: 'notFound' }
  return null
}

/** The claims of a room token that say what kind of session it is. */
export type ShareClaims = { sub: string | null; shareOnly: boolean; preapproved: boolean }

/** Read, not verified — the server verifies. Unreadable reads as an ordinary session. */
export function readShareClaims(roomToken: string | null | undefined): ShareClaims {
  try {
    const b64 = String(roomToken).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(b64)) as Record<string, unknown>
    return {
      sub: typeof claims.sub === 'string' ? claims.sub : null,
      shareOnly: claims.share_only === true,
      preapproved: claims.share_preapproved === true,
    }
  } catch {
    return { sub: null, shareOnly: false, preapproved: false }
  }
}

/** The next step after a share-mode join (not a check). */
export type ShareJoinStep =
  | {
      kind: 'waiting'
      shareId: string
      requestId: string
      waitingFor: WaitingFor
      meeting: ShareJoinMeeting
      /** Signaling's lobby channel, where the decision is pushed; absent, it is polled for. */
      lobby?: LobbyWait
    }
  | {
      kind: 'admitted'
      shareId: string
      meeting: ShareJoinMeeting
      credentials: RoomCredentials
      /** The meeting owner's own page (`share_preapproved`): only "alone" ends it. */
      ownerPage: boolean
    }
  | { kind: 'refused'; refusal: ShareJoinRefusal }

const unavailable: ShareJoinStep = { kind: 'refused', refusal: { kind: 'unavailable' } }

export function shareJoinStep(res: ShareJoinResult): ShareJoinStep {
  if (res.mode !== 'share') return unavailable
  if (res.status === 'waiting') {
    if (!isShareOnlyPeer(res.shareId) || !res.requestId) return unavailable
    return {
      kind: 'waiting',
      shareId: res.shareId,
      requestId: res.requestId,
      // Absent from an older server; admission is what it meant there.
      waitingFor: res.waitingFor ?? 'admission',
      meeting: res.meeting,
      ...(res.lobby ? { lobby: res.lobby } : {}),
    }
  }
  if (res.status === 'admitted') return admitShare(res.shareId, res.meeting, res.roomCredentials)
  return unavailable
}

/**
 * Admit with these credentials — only if they are what share mode promises.
 *
 * The token has to name the share identity and carry `share_only`. Anything
 * else would put this page in the meeting as a full participant, under the
 * account's own id, colliding with that account's real session — so it is
 * refused rather than used (the backend refuses the same way when signaling
 * could not mint the claim).
 */
export function admitShare(
  shareId: string,
  meeting: ShareJoinMeeting,
  credentials: RoomCredentials | null | undefined,
): ShareJoinStep {
  if (!credentials?.channelTopic || !credentials.signalingWsUrl || !credentials.roomToken) return unavailable
  const claims = readShareClaims(credentials.roomToken)
  if (!isShareOnlyPeer(shareId) || claims.sub !== shareId || !claims.shareOnly) return unavailable
  return { kind: 'admitted', shareId, meeting, credentials, ownerPage: claims.preapproved }
}

/**
 * The name this tab is sharing under, kept per meeting.
 *
 * The dashboard hands it over in `?name=`, which the page takes out of the
 * address bar (a name in a URL travels with every copy of the link). Kept in
 * sessionStorage so a reload still shares under the name that was typed, and
 * only in this tab.
 */
const NAME_KEY = (meetingId: string) => `inmeet_share_name:${meetingId}`

export function rememberShareName(meetingId: string, name: string): void {
  try {
    sessionStorage.setItem(NAME_KEY(meetingId), name)
  } catch {
    /* storage blocked: the name still holds until the page goes */
  }
}

export function rememberedShareName(meetingId: string): string | null {
  try {
    return sessionStorage.getItem(NAME_KEY(meetingId))
  } catch {
    return null
  }
}
